import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listLeadsV2 } from 'services/lead/leadServices';
import { queryKeys } from '../../config/queryClient';

/**
 * Initial Board Load Hook - SINGLE CACHE ARCHITECTURE
 *
 * Makes ONE API call to fetch page 1 for ALL columns, then populates
 * each column's infinite query cache directly.
 *
 * Benefits:
 * - Single API call on load (fast!)
 * - No dual-cache complexity
 * - All data goes directly into infinite query caches
 * - Mutations only need to update one cache type
 *
 * @param {Array} leadStages - Array of lead stage objects with sk property
 * @param {Object} filters - Filter parameters (searchKeyword, practiceArea, assignedTo, sortType)
 * @returns {Object} - Query result with loading, error states
 */
export const useInitialBoardLoad = (leadStages, filters = {}) => {
  const queryClient = useQueryClient();
  const isEnabled = !!leadStages && Array.isArray(leadStages) && leadStages.length > 0;

  return useQuery({
    queryKey: ['leads', 'initial-load', filters, leadStages?.length],

    queryFn: async () => {
      if (!leadStages || leadStages.length === 0) {
        throw new Error('No active lead stages found. Please configure lead stages first.');
      }

      // Build request payload
      const requestData = {
        lead_stage_ids: leadStages.map((stage) => stage.sk),
      };

      // Add filters to body
      if (filters.practiceArea) {
        requestData.practice_area = filters.practiceArea;
      }
      if (filters.assignedTo && filters.assignedTo.length > 0) {
        requestData.assigned_to = filters.assignedTo;
      }
      if (filters.sortType) {
        requestData.sort_type = filters.sortType;
      }

      // Single API call for ALL columns (page 1)
      // Note: keyword is passed as a separate parameter to match API signature
      const response = await listLeadsV2({
        limit: 15,
        data: requestData,
        keyword: filters.searchKeyword || false,
        excludeFromLoading: true,
      });

      // Populate EACH column's infinite query cache with page 1 data
      Object.keys(response?.data || {}).forEach((columnId) => {
        const columnData = response.data[columnId];

        // Set initial data in infinite query format (include filters in key)
        queryClient.setQueryData([...queryKeys.leads.column(columnId), filters], {
          pages: [
            {
              items: columnData?.items || [],
              page: 1,
              hasMore: columnData?.has_more || false,
              totalCount: columnData?.total_count || 0,
            },
          ],
          pageParams: [1],
        });
      });

      return {
        success: true,
        columnsLoaded: Object.keys(response?.data || {}).length,
      };
    },

    enabled: isEnabled,

    // Use global defaults for consistency
    // staleTime: inherited from global (2 minutes)
    // refetchOnWindowFocus: inherited from global (false - manual refresh via user action)
    // No polling - reduces unnecessary API calls for Kanban boards

    retry: 2,

    // Don't show errors immediately, let components handle it
    useErrorBoundary: false,
  });
};
